/**
 * Minimal radix-2 iterative Cooley-Tukey FFT/IFFT — the one piece of DSP the
 * Impulse/Step Response chart needs (packages/charts/src/impulseResponse.ts)
 * that nothing else in the monorepo already provides. In-place on parallel
 * real/imaginary Float64Arrays; length MUST be a power of 2.
 */

function isPowerOfTwo(n: number): boolean {
  return n > 0 && (n & (n - 1)) === 0;
}

function bitReversePermute(re: Float64Array, im: Float64Array): void {
  const n = re.length;
  for (let i = 1, j = 0; i < n; i++) {
    let bit = n >> 1;
    for (; j & bit; bit >>= 1) j ^= bit;
    j ^= bit;
    if (i < j) {
      const tr = re[i]!; re[i] = re[j]!; re[j] = tr;
      const ti = im[i]!; im[i] = im[j]!; im[j] = ti;
    }
  }
}

function fftCore(re: Float64Array, im: Float64Array, inverse: boolean): void {
  const n = re.length;
  if (re.length !== im.length) throw new Error('fft: re/im length mismatch');
  if (!isPowerOfTwo(n)) throw new Error(`fft: length must be a power of 2, got ${n}`);

  bitReversePermute(re, im);

  for (let len = 2; len <= n; len <<= 1) {
    const half = len >> 1;
    const angStep = (inverse ? 2 : -2) * Math.PI / len;
    for (let i = 0; i < n; i += len) {
      for (let k = 0; k < half; k++) {
        const ang = angStep * k;
        const wr = Math.cos(ang);
        const wi = Math.sin(ang);
        const aIdx = i + k;
        const bIdx = i + k + half;
        const bRe = re[bIdx]! * wr - im[bIdx]! * wi;
        const bIm = re[bIdx]! * wi + im[bIdx]! * wr;
        const aRe = re[aIdx]!;
        const aIm = im[aIdx]!;
        re[aIdx] = aRe + bRe;
        im[aIdx] = aIm + bIm;
        re[bIdx] = aRe - bRe;
        im[bIdx] = aIm - bIm;
      }
    }
  }

  if (inverse) {
    for (let i = 0; i < n; i++) {
      re[i]! /= n;
      im[i]! /= n;
    }
  }
}

/** Forward FFT, in place. `re.length` must be a power of 2. */
export function fft(re: Float64Array, im: Float64Array): void {
  fftCore(re, im, false);
}

/** Inverse FFT, in place (includes the 1/n normalisation). `re.length` must
 *  be a power of 2. */
export function ifft(re: Float64Array, im: Float64Array): void {
  fftCore(re, im, true);
}

export { isPowerOfTwo };

/** Smallest power of 2 >= n. */
export function nextPowerOfTwo(n: number): number {
  if (n <= 1) return 1;
  return 2 ** Math.ceil(Math.log2(n));
}
